import {
	CircleAlert,
	LockKeyhole,
	type LucideIcon,
	ServerCrash,
	WifiOff,
} from 'lucide-react';
import type { FallbackProps } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import { AuthError, NetworkError, ServerError } from '@/shared/lib';
import Button from '../button';

type ErrorContent = {
	Icon: LucideIcon;
	title: string;
	description: string;
	buttonLabel: string;
	action: () => void;
};

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
	const navigate = useNavigate();

	const content: ErrorContent = (() => {
		if (error instanceof AuthError) {
			return {
				Icon: LockKeyhole,
				title: '로그인이 필요해요',
				description: '이 페이지를 보려면 로그인이 필요해요',
				buttonLabel: '로그인하기',
				action: () => {
					resetErrorBoundary();
					navigate('/login');
				},
			};
		}
		if (error instanceof NetworkError) {
			return {
				Icon: WifiOff,
				title: '네트워크 연결을 확인해주세요',
				description: '인터넷 연결이 끊겼어요. 연결 후 다시 시도해 주세요',
				buttonLabel: '다시 시도',
				action: resetErrorBoundary,
			};
		}
		if (error instanceof ServerError) {
			return {
				Icon: ServerCrash,
				title: '서버 오류가 발생했어요',
				description: '일시적인 오류예요. 잠시 후 다시 시도해 주세요',
				buttonLabel: '다시 시도',
				action: resetErrorBoundary,
			};
		}
		return {
			Icon: CircleAlert,
			title: '오류가 발생했어요',
			description: '알 수 없는 오류예요. 잠시 후 다시 시도해 주세요',
			buttonLabel: '다시 시도',
			action: resetErrorBoundary,
		};
	})();

	const { Icon, title, description, buttonLabel, action } = content;

	return (
		<div className="mx-auto flex h-dvh max-w-md flex-col items-center justify-center gap-6 px-5 font-pretendard shadow-md">
			<Icon className="text-mantang-gray-300" size={56} />
			<div className="flex flex-col items-center gap-2 text-center">
				<h2 className="font-semibold text-[20px] text-mantang-black">
					{title}
				</h2>
				<p className="text-base text-mantang-gray-500 leading-7">
					{description}
				</p>
			</div>
			<Button type="button" onClick={action}>
				{buttonLabel}
			</Button>
		</div>
	);
};

export default ErrorFallback;
